import { acceptHMRUpdate, defineStore } from 'pinia';
import type { Address } from 'viem';

import { computed, ref, useNuxtApp } from '#imports';
import { useAlertsStore } from '@/stores/alerts/useAlertsStore';

export interface IToken {
    exp: number;
    iat?: number;
    user_id?: string;
}

export interface SearchCollectionItem {
    imageUrl: string;
    floorPrice: Price;
    name: string;
    slug: string;
    link: string;
}

export type SearchCollectionItems = SearchCollectionItem[];

export const useUserStore = defineStore(
    'account',
    () => {
        const user = ref<User['address'] | undefined>(undefined);
        const access = ref<Nullable<string>>(null);
        const refresh = ref<Nullable<string>>(null);
        const chainId = ref<User['chainId'] | undefined>(undefined);
        const recentSearches = ref<SearchCollectionItems>([]);
        let readyResolver: (() => void) | null = null;
        const ready = new Promise<void>(resolve => readyResolver = resolve);
        const isReady = ref<boolean>(false);
        const isHydrated = ref(false);

        function setIsHydrated() {
            isHydrated.value = true;
        }

        function setReady() {
            isReady.value = true;
            if (readyResolver) readyResolver();
        }

        const alertStore = useAlertsStore();

        const { $wagmi, $evmCore } = useNuxtApp();

        const chainMap = computed(() => {
            if ($evmCore) {
                const { map: { chainCurrencyMap } } = $evmCore.configs.getConfigFields('evm', (['map']));
                return chainCurrencyMap;
            }
            else {
                return {
                    1: 'eth',
                    137: 'matic',
                    555272: 'xsollaZk'
                } as const;
            }
        });

        const network = computed(() => {
            return chainId.value ? chainMap.value[chainId.value] : null;
        });

        const addRecentSearch = (search: SearchCollectionItem) => {
            if (!recentSearches.value.some(item => item.slug === search.slug)) {
                if (recentSearches.value.length >= 2) {
                    recentSearches.value.pop();
                    recentSearches.value.unshift(search);
                }
                else {
                    recentSearches.value.push(search);
                }
            }
        };

        const getRecentSearches = () => {
            return recentSearches.value;
        };

        const showUserIsDisconnect = () => {
            alertStore.add({
                title: 'Oops, something went wrong',
                description: 'Please login to your wallet to continue your purchase',
                critical: false,
                status: 'type.info'
            });
            setTimeout(
                () => $wagmi?.wallet.setVisibleOpenWallet(true),
                500
            );
        };

        const setAuth = (accessToken: string, refreshToken: string) => {
            access.value = accessToken;
            refresh.value = refreshToken;
        };

        const setNewUser = (newUser: Address) => {
            user.value = newUser;
        };

        const signIn = ({ address, id, accessToken, refreshToken }: {
            address: Address;
            id?: User['chainId'];
            accessToken: string;
            refreshToken: string;
        }) => {
            user.value = address;
            setAuth(accessToken, refreshToken);
            if (id && chainMap.value[id]) {
                chainId.value = id;
            }
            else {
                chainId.value = undefined;
            }
        };

        const switchNetwork = (id: User['chainId']) => {
            if (chainMap.value[id]) chainId.value = id;
            else chainId.value = undefined;
        };

        const getTokenExpiration = (time: number): number => {
            const token = access.value;
            if (token) {
                const [, payload] = token.split('.');
                const tokenData: IToken = JSON.parse(atob(payload as string)) as IToken;
                const expiresAt: number = tokenData.exp * 1000;
                return expiresAt - Date.now() - time;
            }
            else {
                return 0;
            }
        };

        const signOut = () => {
            user.value = undefined;
            chainId.value = undefined;
            access.value = null;
            refresh.value = null;
        };

        return {
            user,
            isReady,
            isHydrated,
            ready,
            network,
            chainId,
            access,
            refresh,
            recentSearches,
            addRecentSearch,
            getRecentSearches,
            setNewUser,
            setAuth,
            setReady,
            setIsHydrated,
            signIn,
            signOut,
            switchNetwork,
            showUserIsDisconnect,
            getTokenExpiration
        } as const;
    },
    {
        persist: {
            pick: [
                'user',
                'chainId',
                'access',
                'recentSearches',
                'refresh',
                'network'
            ]
        }
    }

);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(
        useUserStore,
        import.meta.hot
    ));
}
